import React, { useEffect, useState, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { IoClose, IoMenu, IoChevronDown } from "react-icons/io5";
import {
    MdOutlinePolicy, MdOutlineDashboard, MdDashboard, MdPolicy, MdGroup,
    MdReadMore, MdOutlineInbox, MdOutlineAnalytics, MdAnalytics,
    MdOutlineNumbers, MdScreenshot, MdOutlineScreenshot,
    MdOutlineReceiptLong, MdReceiptLong
} from "react-icons/md";
import { MdOutlineGroup } from "react-icons/md";
import { BsBadgeAd, BsBadgeAdFill } from "react-icons/bs";
import { FaBug, FaRegEye, FaRegHandshake } from "react-icons/fa";
import { BiPurchaseTag } from 'react-icons/bi';
import { AiFillNotification, AiOutlineApi, AiOutlineHistory } from 'react-icons/ai';
import { CiMicrochip } from "react-icons/ci";
import { HiOutlineChartBar } from "react-icons/hi";
import NLogo from '../../Image/NLogo.svg';

const Navbar1 = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [expandedMenus, setExpandedMenus] = useState({});
    const [hoveredItem, setHoveredItem] = useState(null);
    const location = useLocation();

    const navItems = [
        {
            id: 'dashboard',
            path: '/',
            label: 'Dashboard',
            icon: (active) => active ? <MdDashboard size={20} /> : <MdOutlineDashboard size={20} />,
        },
        {
            id: 'policy',
            path: '/policy',
            label: 'Policy',
            icon: (active) => active ? <MdPolicy size={20} /> : <MdOutlinePolicy size={20} />,
        },
        {
            id: 'user',
            path: '/manage/user/all/data',
            label: 'User Manager',
            icon: (active) => active ? <MdGroup size={20} /> : <MdOutlineGroup size={20} />,
        },
        {
            id: 'ads',
            path: '/manage/ads/nativeAds/add',
            label: 'Ads Manager',
            icon: (active) => active ? <BsBadgeAdFill size={20} /> : <BsBadgeAd size={20} />,
        },
        {
            id: 'adstrack',
            path: '/manage/adstrack',
            label: 'Ads Analytics',
            icon: () => <HiOutlineChartBar size={20} />,
        },
        {
            id: 'showboxanalytic',
            path: '/manage/showboxanalytic',
            label: 'Box Analytics',
            icon: (active) => active ? <MdAnalytics size={20} /> : <MdOutlineAnalytics size={20} />,
        },
        {
            id: 'UI',
            path: '/manage/UI/jsonmanager',
            label: 'UI Manager',
            icon: (active) => active ? <MdScreenshot size={20} /> : <MdOutlineScreenshot size={20} />,
        },
        {
            id: 'ratelimit',
            path: '/manage/ratelimit',
            label: 'IP Manager',
            icon: () => <CiMicrochip size={20} />,
        },
        {
            id: 'plan',
            label: 'Plan Manager',
            icon: (active) => active ? <MdReceiptLong size={20} /> : <MdOutlineReceiptLong size={20} />,
            hasSubmenu: true,
            submenu: [
                { id: 'plan-view', path: '/manage/plan/', label: 'Plan View', icon: () => <FaRegEye size={14} /> },
                { id: 'plan-history', path: '/manage/plan/history', label: 'Plan History', icon: () => <BiPurchaseTag size={14} /> },
                { id: 'referral', path: '/manage/plan/referral/', label: 'Referral Plan', icon: () => <FaRegHandshake size={14} /> },
                { id: 'referral-history', path: '/manage/plan/referral/history', label: 'Referral History', icon: () => <AiOutlineHistory size={14} /> },
            ]
        },
        {
            id: 'mobile',
            label: 'Mobile',
            icon: () => <MdReadMore size={20} />,
            hasSubmenu: true,
            submenu: [
                { id: 'mobile-view', path: '/manage/mobile/', label: 'View', icon: () => <FaRegEye size={14} /> },
                { id: 'showbox', path: '/manage/mobile/showbox/', label: 'Show Box', icon: () => <MdOutlineInbox size={14} /> },
                { id: 'whitelist', path: '/manage/mobile/whitelistnumber', label: 'Whitelist Numbers', icon: () => <MdOutlineNumbers size={14} /> },
                { id: 'bugreport', path: '/manage/mobile/bugreport', label: 'Bug Report', icon: () => <FaBug size={14} /> },
                { id: 'api-config', path: '/manage/mobile/api-config', label: 'API Config', icon: () => <AiOutlineApi size={14} /> },
                { id: 'auto-notify', path: '/manage/mobile/autonotification', label: 'Auto Notification', icon: () => <AiFillNotification size={14} /> },
            ]
        }
    ];

    // Responsive check
    useEffect(() => {
        const checkMobile = () => {
            const mobile = window.innerWidth < 768;
            setIsMobile(mobile);
            if (mobile) setIsOpen(false);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Auto-expand active submenu
    useEffect(() => {
        navItems.forEach(item => {
            if (item.hasSubmenu && item.submenu.some(sub => location.pathname === sub.path)) {
                setExpandedMenus(prev => ({ ...prev, [item.id]: true }));
            }
        });
    }, [location.pathname]);

    const handleMouseEnter = () => !isMobile && setIsOpen(true);
    const handleMouseLeave = () => !isMobile && setIsOpen(false);
    const toggleMenu = useCallback(() => setIsOpen(prev => !prev), []);
    const handleMobileClick = useCallback(() => isMobile && setIsOpen(false), [isMobile]);

    const toggleSubmenu = useCallback((id, e) => {
        e.preventDefault();
        e.stopPropagation();
        setExpandedMenus(prev => ({ ...prev, [id]: !prev[id] }));
    }, []);

    const getSelectedIcon = (itemId) => {
        const pathArray = location.pathname.split('/');
        return pathArray.includes(itemId) || (itemId === 'dashboard' && location.pathname === '/');
    };

    const hasActiveSubmenu = (item) => {
        return item.hasSubmenu && item.submenu.some(sub => location.pathname === sub.path);
    };

    const isSubmenuActive = (path) => location.pathname === path;

    return (
        <>
            {/* Mobile Toggle Button */}
            {isMobile && (
                <button
                    className="fixed top-4 left-4 z-[60] group"
                    onClick={toggleMenu}
                    aria-label="Toggle navigation"
                >
                    <div className="relative p-3 rounded-2xl bg-main-blue text-main-white shadow-lg active:scale-95 transition-all duration-300">
                        <IoMenu
                            size={22}
                            className={`transition-all duration-300 ${isOpen ? 'rotate-180 opacity-0 scale-50' : 'rotate-0 opacity-100 scale-100'}`}
                        />
                        <IoClose
                            size={22}
                            className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 
                                        transition-all duration-300 ${isOpen ? 'rotate-0 opacity-100 scale-100' : '-rotate-180 opacity-0 scale-50'}`}
                        />
                    </div>
                </button>
            )}

            {/* Sidebar */}
            <div
                className={`
                    fixed top-0 left-0 z-50 h-screen
                    transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]
                    ${isMobile
                        ? (isOpen ? 'w-72 translate-x-0' : 'w-0 -translate-x-full')
                        : (isOpen ? 'w-60' : 'w-[70px]')
                    }
                `}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <nav className="relative bg-main-white h-full shadow-xl border-r border-main-border flex flex-col overflow-hidden">
                    {/* Top Accent Line */}
                    <div className="absolute top-0 left-0 right-0 h-1 bg-main-blue" />

                    {/* Logo Header - Fixed height 72px to match Navbar2 */}
                    <div className="relative z-10 h-[72px] flex items-center px-4 border-b border-main-border bg-main-white flex-shrink-0">
                        <div className={`flex items-center ${!isOpen && !isMobile ? 'justify-center w-full' : 'gap-3'}`}>
                            <div className="w-10 h-10 rounded-xl bg-main-blue flex items-center justify-center shadow-lg flex-shrink-0 overflow-hidden">
                                <img src={NLogo} alt="Logo" className="w-7 h-7 object-contain" />
                            </div>
                            {(isOpen || (isMobile && isOpen)) && (
                                <div className="flex flex-col min-w-0">
                                    <span className="text-base font-bold text-main-font truncate">Admin Panel</span>
                                    <span className="text-[10px] text-main-subfont uppercase tracking-wider">Caller Name</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Navigation Items */}
                    <div className="flex-1 py-4 px-3 overflow-y-auto overflow-x-hidden custom-scrollbar relative z-10">
                        <ul className="space-y-1">
                            {navItems.map((item, index) => {
                                const selected = getSelectedIcon(item.id);
                                const isExpanded = expandedMenus[item.id];
                                const hasActiveSub = hasActiveSubmenu(item);
                                const isHovered = hoveredItem === item.id;

                                return (
                                    <li
                                        key={item.id}
                                        style={{ animationDelay: `${index * 30}ms` }}
                                        className="animate-fadeIn"
                                    >
                                        {item.hasSubmenu ? (
                                            <div>
                                                {/* Parent Menu Button */}
                                                <button
                                                    onClick={(e) => toggleSubmenu(item.id, e)}
                                                    onMouseEnter={() => setHoveredItem(item.id)}
                                                    onMouseLeave={() => setHoveredItem(null)}
                                                    className={`
                                                        relative w-full flex items-center justify-between py-1 rounded-xl
                                                        transition-all duration-200 group
                                                        ${selected || hasActiveSub
                                                            ? 'bg-main-blue text-main-white'
                                                            : 'text-main-subfont hover:bg-main-bg'
                                                        }
                                                        ${!isOpen && !isMobile ? 'justify-center' : ''}
                                                    `}
                                                    title={!isOpen && !isMobile ? item.label : ''}
                                                >
                                                    {/* Active Indicator Line */}
                                                    {(selected || hasActiveSub) && (
                                                        <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-white/40 rounded-r-full" />
                                                    )}

                                                    <div className="flex items-center min-w-0 flex-1">
                                                        <div className={`
                                                            flex items-center justify-center w-10 h-10 rounded-xl flex-shrink-0
                                                            transition-all duration-200
                                                            ${selected || hasActiveSub
                                                                ? ''
                                                                : 'bg-main-bg group-hover:bg-main-thHover group-hover:text-main-blue'
                                                            }
                                                        `}>
                                                            {item.icon(selected || hasActiveSub)}
                                                        </div>
                                                        {(isOpen || (isMobile && isOpen)) && (
                                                            <span className="ml-3 font-medium text-[13px] truncate tracking-wide">
                                                                {item.label}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {(isOpen || (isMobile && isOpen)) && (
                                                        <div className={`
                                                            flex-shrink-0 ml-2 transition-transform duration-300
                                                            ${isExpanded ? 'rotate-180' : 'rotate-0'}
                                                        `}>
                                                            <IoChevronDown size={14} />
                                                        </div>
                                                    )}

                                                    {/* Tooltip for collapsed state */}
                                                    {!isOpen && !isMobile && isHovered && (
                                                        <div className="absolute left-full ml-3 px-3 py-1.5 bg-main-font text-main-white text-xs font-medium 
                                                                        rounded-lg whitespace-nowrap z-50 shadow-xl animate-tooltipIn">
                                                            {item.label}
                                                            <span className="absolute -left-1.5 top-1/2 -translate-y-1/2 w-3 h-3 bg-main-font rotate-45" />
                                                        </div>
                                                    )}
                                                </button>

                                                {/* Submenu Items */}
                                                <div className={`
                                                    overflow-hidden transition-all duration-300 ease-out
                                                    ${(isOpen || (isMobile && isOpen)) && isExpanded
                                                        ? 'max-h-[400px] opacity-100 mt-1'
                                                        : 'max-h-0 opacity-0'
                                                    }
                                                `}>
                                                    <ul className="ml-5 pl-4 border-l-2 border-main-border space-y-0.5 py-1">
                                                        {item.submenu.map((subItem, subIndex) => {
                                                            const subActive = isSubmenuActive(subItem.path);
                                                            return (
                                                                <li
                                                                    key={subItem.id}
                                                                    style={{ animationDelay: `${subIndex * 50}ms` }}
                                                                    className={isExpanded ? 'animate-slideIn' : ''}
                                                                >
                                                                    <Link
                                                                        to={subItem.path}
                                                                        onClick={handleMobileClick}
                                                                        className={`
                                                                            relative flex items-center gap-2.5 px-3 py-2 rounded-lg
                                                                            transition-all duration-200 text-[13px]
                                                                            ${subActive
                                                                                ? 'bg-main-th text-main-blue font-semibold'
                                                                                : 'text-main-subfont hover:bg-main-bg'
                                                                            }
                                                                        `}
                                                                    >
                                                                        {/* Sub Active Dot */}
                                                                        {subActive && (
                                                                            <span className="absolute -left-[18px] top-1/2 -translate-y-1/2 w-2 h-2 bg-main-blue rounded-full" />
                                                                        )}
                                                                        <span className={`flex-shrink-0 ${subActive ? 'text-main-blue' : 'text-main-subfont'}`}>
                                                                            {subItem.icon(subActive)}
                                                                        </span>
                                                                        <span className="truncate">{subItem.label}</span>
                                                                    </Link>
                                                                </li>
                                                            );
                                                        })}
                                                    </ul>
                                                </div>
                                            </div>
                                        ) : (
                                            <Link
                                                to={item.path}
                                                onClick={handleMobileClick}
                                                onMouseEnter={() => setHoveredItem(item.id)}
                                                onMouseLeave={() => setHoveredItem(null)}
                                                className={`
                                                    relative flex items-center px-2 py-1 rounded-xl
                                                    transition-all duration-200 group
                                                    ${selected
                                                        ? 'bg-main-blue text-main-white'
                                                        : 'text-main-subfont hover:bg-main-bg'
                                                    }
                                                    ${!isOpen && !isMobile ? 'justify-center' : ''}
                                                `}
                                                title={!isOpen && !isMobile ? item.label : ''}
                                            >
                                                {/* Active Indicator Line */}
                                                {selected && (
                                                    <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-white/40 rounded-r-full" />
                                                )}

                                                <div className={`
                                                    flex items-center justify-center w-10 h-10 rounded-xl flex-shrink-0
                                                    transition-all duration-200
                                                    ${selected
                                                        ? ''
                                                        : 'bg-main-bg group-hover:bg-main-thHover group-hover:text-main-blue'
                                                    }
                                                `}>
                                                    {item.icon(selected)}
                                                </div>
                                                {(isOpen || (isMobile && isOpen)) && (
                                                    <span className="ml-3 font-medium text-[13px] truncate tracking-wide">
                                                        {item.label}
                                                    </span>
                                                )}

                                                {/* Tooltip for collapsed state */}
                                                {!isOpen && !isMobile && isHovered && (
                                                    <div className="absolute left-full ml-3 px-3 py-1.5 bg-main-font text-main-white text-xs font-medium 
                                                                    rounded-lg whitespace-nowrap z-50 shadow-xl animate-tooltipIn">
                                                        {item.label}
                                                        <span className="absolute -left-1.5 top-1/2 -translate-y-1/2 w-3 h-3 bg-main-font rotate-45" />
                                                    </div>
                                                )}
                                            </Link>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    </div>

                    {/* Footer */}
                    <div className="relative z-10 p-4 border-t border-main-border flex-shrink-0">
                        {(isOpen || (isMobile && isOpen)) ? (
                            <div className="flex items-center gap-3 px-2">
                                <div className="w-2 h-2 rounded-full bg-main-blue animate-pulse" />
                                <span className="text-xs text-main-subfont font-medium">System Online</span>
                            </div>
                        ) : (
                            <div className="flex justify-center">
                                <div className="w-2 h-2 rounded-full bg-main-blue animate-pulse" />
                            </div>
                        )}
                    </div>
                </nav>
            </div>

            {/* Mobile Overlay */}
            {isMobile && (
                <div
                    className={`
                        fixed inset-0 z-30 bg-black/50
                        transition-all duration-300
                        ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}
                    `}
                    onClick={toggleMenu}
                />
            )}

            {/* CSS Animations */}
            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateX(-8px); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes slideIn {
                    from { opacity: 0; transform: translateX(-12px); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes tooltipIn {
                    from { opacity: 0; transform: translateX(-4px); }
                    to { opacity: 1; transform: translateX(0); }
                }
                .animate-fadeIn {
                    animation: fadeIn 0.3s ease-out forwards;
                }
                .animate-slideIn {
                    animation: slideIn 0.25s ease-out forwards;
                }
                .animate-tooltipIn {
                    animation: tooltipIn 0.15s ease-out forwards;
                }
                .custom-scrollbar {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
                .custom-scrollbar::-webkit-scrollbar {
                    display: none;
                }
            `}</style>
        </>
    );
};

export default Navbar1;